import { memo, useCallback, useEffect, useState } from 'react'
import { Modal } from 'components/layouts/modal/modal'
import { useTranslation } from '@crew/modules/i18n'
import { apiBaseUrl } from '@crew/configs/constants'
import { FileUploader } from 'devextreme-react'
import classNames from 'classnames'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { useToast } from 'hooks/useToast'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useTenantUserBulkAddDialog } from './useTenantUserBulkAddDialog'
import { BulkAddUserTable } from './components/bulkAddUserTable/bulkAddUserTable'
import { useGetInviteableUsersQuery } from '@crew/apis/user/userApis'
import { ContractPlan, InvitationRoleType } from '@crew/enums/app'
import { useAppSelector } from 'states/hooks'
import { VerifyBulkInsertUsersResponse } from '@crew/apis/user/models/verifyBulkInsertUsers/response'
import { GetInviteableUsersResponse } from '@crew/apis/user/models/getInviteableUsers/response'

export type PropsMode = {
  title: string
  isOpen: boolean
  onClose: () => void
}

export const TenantUserBulkAddDialog = memo((props: PropsMode) => {
  const { t } = useTranslation()

  const toast = useToast()

  const tenantSettingUserEventMessage = useAppSelector(
    (state) => state.app.tenantSettingUserEventMessage
  )
  const currentPlan = useAppSelector((state) => state.app.currentPlan)

  const { verifyBulkAddUsers, bulkAddUsers, isLoadingBulkAddUsers } =
    useTenantUserBulkAddDialog()

  const [fileName, setFileName] = useState<string>()
  const [showApiErrors] = useShowApiErrors()

  // 招待可能なユーザー数を取得
  const {
    data: getInviteableUsersResult,
    refetch: refetchInviteableUsersResult,
  } = useGetInviteableUsersQuery()

  // refetch inviteable users/external users when tenant setting member updated
  useEffect(() => {
    if (tenantSettingUserEventMessage) {
      refetchInviteableUsersResult()
    }
  }, [tenantSettingUserEventMessage, currentPlan, refetchInviteableUsersResult])

  const [verifyBulkAddUsersResult, setVerifyBulkAddUsersResult] =
    useState<VerifyBulkInsertUsersResponse>()

  // ダウンロードCSVテンプレート
  const handleDownloadCsvTemplate = useCallback(() => {
    window.location.href = `${apiBaseUrl()}api/v1/users/bulk-add/template`
  }, [])

  // ファイルアップロード
  const handleFileUpload = useCallback(
    async (file: File) => {
      setFileName(file.name)
      try {
        const result = await verifyBulkAddUsers(file)

        setVerifyBulkAddUsersResult(result)
      } catch (error) {
        showApiErrors(error)
      }
    },
    [showApiErrors, verifyBulkAddUsers]
  )

  // Event handler when close dialog
  const handleCloseDialog = useCallback(() => {
    props.onClose()
    setFileName(undefined)
    setVerifyBulkAddUsersResult(undefined)
  }, [props])

  // Event handler when click add users button
  const handleAddUsersButtonClick = useCallback(async () => {
    try {
      if (!verifyBulkAddUsersResult) return

      const externalUsersCount = verifyBulkAddUsersResult.data.filter(
        (user) => user.roleCode.value === InvitationRoleType.External.key
      ).length

      const internalUsersCount =
        verifyBulkAddUsersResult.data.length - externalUsersCount

      const inviteableUsers =
        getInviteableUsersResult ??
        ({
          inviteableUsers: 0,
          inviteableExternalUsers: 0,
          currentExternalUsers: 0,
          currentUsers: 0,
          remainingInviteableUsers: 0,
          remainingInviteableExternalUsers: 0,
          totalRemainingInviteableUsers: 0,
        } satisfies GetInviteableUsersResponse)

      // 内部ユーザーの空き枠数が足りているか
      if (internalUsersCount > inviteableUsers.remainingInviteableUsers) {
        toast.error(
          t('message.apiError.createTenantUsersExceedLimit', {
            remainingUser: inviteableUsers.remainingInviteableUsers,
          })
        )
        return
      }

      // 外部ユーザーの空き枠数が足りているか
      if (
        externalUsersCount > inviteableUsers.remainingInviteableExternalUsers
      ) {
        toast.error(
          t('message.apiError.addExternalUserLimitExceeded', {
            inviteableExternalUserCount:
              inviteableUsers.inviteableExternalUsers,
            currentExternalUserCount: inviteableUsers.currentExternalUsers,
            exceededExternalUserCount:
              externalUsersCount -
              inviteableUsers.remainingInviteableExternalUsers,
          })
        )
        return
      }

      // 内部ユーザー・外部ユーザー共通の空き枠数が足りているか。
      // 内部ユーザー単体、外部ユーザー単体での枠数上限判定は既に行っているので、
      // ここの判定でひっかかるのは、フリープランなど、内部ユーザーと外部ユーザーの枠が合算されて上限判定される場合のみ。
      if (
        internalUsersCount + externalUsersCount >
        inviteableUsers.totalRemainingInviteableUsers
      ) {
        toast.error(
          // 内部ユーザ・外部ユーザ共通なので、両者を区別しない「ユーザー数が制限を超えた」というメッセージを表示する
          t('message.apiError.createTenantUsersExceedLimit', {
            remainingUser: inviteableUsers.totalRemainingInviteableUsers,
          })
        )
      }

      await bulkAddUsers(verifyBulkAddUsersResult)

      // Close dialog
      handleCloseDialog()

      toast.success(t('message.tenant.bulkAddUsersSuccess'))
    } catch (error) {
      showApiErrors(error)
    }
  }, [
    bulkAddUsers,
    getInviteableUsersResult,
    handleCloseDialog,
    showApiErrors,
    t,
    toast,
    verifyBulkAddUsersResult,
  ])

  return (
    <Modal
      title={props.title}
      isOpen={props.isOpen}
      onClose={handleCloseDialog}
    >
      {/* モーダルの最小幅を制限し、画面の各入力項目が正しく表示されるようにする */}
      <div className="p-2.5 flex flex-col gap-y-2.5 min-w-[800px]">
        <p>{t('label.bulkAddUsersDescription')}</p>

        {/* CSVファイルのテンプレートをダウンロード */}
        <p className="crew-link" onClick={handleDownloadCsvTemplate}>
          {t('label.downloadCsvTemplate')}
        </p>

        {/* Note */}
        <div>
          <p>{t('label.csvTemplateNote1')}</p>
          <p>{t('label.csvTemplateNote2')}</p>
          <p>{t('label.csvTemplateNote3')}</p>
        </div>

        {/* ファイルドロップ領域 */}
        <div
          id="dropZone"
          className={classNames(
            'h-24 flex items-center justify-center',
            'border-2 border-dashed', // 線
            'bg-crew-gray-1-light dark:bg-crew-gray-4-dark' // 背景色
          )}
        >
          <p>{fileName ?? t('label.dragAndDropCsvFile')}</p>
        </div>

        {/* ファイルアップローダー */}
        <FileUploader
          dropZone="#dropZone"
          visible={false} //カスタムドロップ領域のみ表示するため、FileUploader本体は表示しない
          uploadMode="instantly"
          uploadFile={handleFileUpload}
          dialogTrigger="#dropZone"
          allowedFileExtensions={['.csv']}
          accept="text/csv"
        />

        {/* ユーザー一覧 */}
        <div className="flex-1 mt-3">
          {
            // 招待内容の確認でエラーが発生した場合にメッセージを表示する。
            verifyBulkAddUsersResult?.errorCount &&
            verifyBulkAddUsersResult.errorCount > 0 ? (
              <p className="mb-1 text-sm crew-error-text">
                {t('message.tenant.batchInviteError', {
                  count: verifyBulkAddUsersResult.errorCount,
                })}
              </p>
            ) : null
          }

          {
            // 「組織のドメインと異なるユーザーが管理者・一般ユーザーとして追加されます。」警告が発生した場合にメッセージを表示する。
            verifyBulkAddUsersResult?.hasWarning && (
              <p className="mb-1 text-sm crew-warning-text">
                {t('message.tenant.externalUser')}
              </p>
            )
          }

          {
            // 「既に組織に登録済みのユーザーがいます。そのユーザーの追加は行われません。」警告が発生した場合にメッセージを表示する。
            verifyBulkAddUsersResult?.hasRegisterdUser && (
              <p className="mb-1 text-sm crew-warning-text">
                {t('message.tenant.existRegisteredUserAdded')}
              </p>
            )
          }

          <BulkAddUserTable addUsers={verifyBulkAddUsersResult?.data ?? []} />
        </div>

        <div className="flex flex-row gap-x-2.5 items-center">
          <p className="mr-auto">
            {/* 追加可能なユーザー数 */}
            {t('label.hintForAllowAddableUsers', {
              remaining:
                getInviteableUsersResult?.remainingInviteableUsers ?? 0,
              total: getInviteableUsersResult?.inviteableUsers ?? 0,
            })}

            {/* 追加可能な外部ユーザー数（有償プランの場合のみ表示） */}
            {(currentPlan === ContractPlan.Professional ||
              currentPlan === ContractPlan.Standard) && (
              <>
                <br />
                {t('label.hintForAllowAddableExternalUsers', {
                  remaining:
                    getInviteableUsersResult?.remainingInviteableExternalUsers ??
                    0,
                  total: getInviteableUsersResult?.inviteableExternalUsers ?? 0,
                })}
              </>
            )}
          </p>

          {/* ユーザーを追加 */}
          <CrewButton
            text={t('action.addUser')}
            type="primary"
            onClick={handleAddUsersButtonClick}
            disabled={
              !verifyBulkAddUsersResult?.data ||
              verifyBulkAddUsersResult.errorCount > 0 ||
              isLoadingBulkAddUsers
            }
          />

          {/* キャンセルボタン */}
          <CrewButton
            text={t('action.cancel')}
            type="normal"
            stylingMode="outlined"
            onClick={handleCloseDialog}
          />
        </div>
      </div>
    </Modal>
  )
})
